/**
 * Route-to-Documentation Mapping
 *
 * Maps console route paths to their corresponding documentation slugs on docs.botbat.io.
 *
 * Format:
 *   - Key: Console route path WITHOUT leading slash. Use `:param` for dynamic segments.
 *     Examples: "campaigns", "campaigns/:id", "customers/segments"
 *   - Value: Docs slug WITH leading slash. Must match a slug in docs-manifest.json.
 *     Examples: "/campaigns/overview", "/customers/segments"
 *
 * Adding a new mapping:
 *   1. Add the route key and docs slug to `routeDocsMap` below
 *   2. Ensure the slug exists in botbat-docs/static/docs-manifest.json
 *   3. Run validation: `npx tsx scripts/validate-docs-mapping.ts`
 *   4. The ui/page-header component auto-resolves slugs — no additional wiring needed
 *
 * Validation: `npx tsx scripts/validate-docs-mapping.ts`
 *   - Checks all slugs exist in the docs manifest
 *   - Reports unmapped routes and generic fallbacks
 */
export const DOCS_BASE_URL = "https://docs.botbat.io";

export const routeDocsMap: Record<string, string> = {
  // Home / Dashboard
  "": "/getting-started/quickstart",
  analytics: "/analytics/overview",

  // Campaigns
  campaigns: "/campaigns/overview",
  "campaigns/create": "/campaigns/create",
  "campaigns/:id": "/campaigns/overview",
  "campaigns/:id/edit": "/campaigns/create",

  // Broadcasts
  broadcasts: "/campaigns/overview",
  "broadcasts/create": "/campaigns/create",

  // Templates
  templates: "/templates/overview",
  "templates/create": "/templates/create",
  "templates/:id": "/templates/overview",
  "templates/:id/edit": "/templates/create",

  // Chatbots
  chatbots: "/chatbots/overview",
  "chatbots/create": "/chatbots/create",
  "chatbots/:id": "/chatbots/manage",
  "chatbots/:id/edit": "/chatbots/manage",

  // Workflows
  workflows: "/workflows/overview",
  "workflows/create": "/workflows/create",
  "workflows/studio/:id": "/workflows/create",

  // Channels
  channels: "/channels/overview",
  "channels/create": "/channels/whatsapp",
  "channels/update/:id": "/channels/overview",
  "channels/:id": "/channels/overview",
  "channels/webchat/preview": "/channels/other-channels",

  // Conversations / Inbox
  conversations: "/inbox/overview",
  "conversations/:id": "/inbox/conversations",

  // Tickets
  tickets: "/tickets/overview",
  "tickets/:id": "/tickets/overview",

  // Customers (CDP)
  "customers/dashboard": "/customers/overview",
  "customers/people": "/customers/people",
  "customers/segments": "/customers/segments",
  "customers/events": "/customers/events",
  "customers/labels": "/customers/labels",
  "customers/consent": "/customers/consent",
  "customers/rules": "/customers/rules",
  "customers/definitions": "/customers/events",
  "customers/goals": "/customers/goals",
  "customers/properties": "/customers/properties",
  "customers/personas": "/customers/personas",
  "customers/lists": "/customers/lists",
  cdp: "/customers/overview",
  "cdp/profile/:id": "/customers/people",

  // Users & Roles
  users: "/users/overview",
  roles: "/users/overview",
  "roles/create": "/users/overview",

  // Billing & Account
  "account/billing": "/billing/overview",
  "account/settings": "/settings/overview",
  "account/payment-processing": "/billing/overview",
  "billing/overage-consumption": "/billing/overview",

  // Files
  files: "/files/overview",

  // Integrations
  integrations: "/integrations/overview",

  // Logs
  logs: "/logs/overview",

  // Settings
  "settings/queue-management": "/settings/overview",
  "settings/queues": "/settings/overview",
  "settings/agent-allocation": "/settings/overview",

  // Contact Center
  "contact-center": "/contact-center/overview",
  "contact-center/setup": "/contact-center/overview",
  "call-routing": "/contact-center/overview",
  "call-recording": "/contact-center/overview",

  // Sender Registrations
  "sender-registrations": "/sender-registrations/overview",
  "sender-registrations/whatsapp/register": "/sender-registrations/overview",
  "sender-registrations/sms/register": "/sender-registrations/overview",
  "sender-registrations/whatsapp/details/:id": "/sender-registrations/overview",
  "sender-registrations/sms/details/:id": "/sender-registrations/overview",

  // Meta Partner
  "meta-partner": "/channels/whatsapp",
  "meta-partner/onboarding": "/channels/whatsapp",
  "meta-partner/onboarding/:id": "/channels/whatsapp",

  // Social Listening
  "social-listening": "/social-listening/overview",

  // Onboarding
  onboarding: "/onboarding/overview",

  // Help Center — dedicated page, no docs mapping needed (the page IS the docs hub)
};

/**
 * Resolves the docs slug for a given console pathname.
 * Handles dynamic route params by matching patterns.
 */
export function getDocsSlugForPath(pathname: string): string | null {
  const trimmed = pathname.replace(/^\/|\/$/g, "");

  // Try exact match first
  if (routeDocsMap[trimmed]) {
    return routeDocsMap[trimmed];
  }

  // Try matching with dynamic segments (replace actual values with :param)
  for (const [pattern, slug] of Object.entries(routeDocsMap)) {
    if (!pattern.includes(":")) continue;

    const patternParts = pattern.split("/");
    const pathParts = trimmed.split("/");

    if (patternParts.length !== pathParts.length) continue;

    const matches = patternParts.every(
      (part, i) => part.startsWith(":") || part === pathParts[i],
    );

    if (matches) return slug;
  }

  return null;
}
